import React, { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../../../assets/images/Color_CombatTv_Logo.svg';
import styles from './ChangePassword.module.scss';
import { useUserService } from '../../../services/user.service';
import { Routes } from '../../../types/constants';

const passwordComplexityRule = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,256}$/;
const passwordRequirementsMessage =
    'Use at least 6 characters with uppercase, lowercase, number, and special character.';

const ChangePassword = () => {
    const userService = useUserService();
    const navigate = useNavigate();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const passwordsMatch = newPassword === confirmPassword;
    const isPasswordComplexityValid = passwordComplexityRule.test(newPassword);

    const handleChangePassword = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage(null);
        setSuccessMessage(null);

        if (!passwordsMatch) {
            setErrorMessage('New passwords do not match.');
            return;
        }

        if (!isPasswordComplexityValid) {
            setErrorMessage('New password does not meet requirements.');
            return;
        }

        setLoading(true);
        try {
            await userService.changePassword(currentPassword, newPassword);
            setSuccessMessage('Password changed successfully.');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error) {
            const message = error instanceof Error ? error.message : '';
            setErrorMessage(message || 'Error changing password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className={styles.page}>
            <section className={styles.hero}>
                <div className={styles.heroContent}>
                    <span className={styles.heroTag}>Security</span>
                    <h1 className={styles.heroTitle}>Change your password</h1>
                    <p className={styles.heroSubtitle}>
                        Keep your account protected by choosing a strong password that only you know.
                    </p>
                </div>
                <div className={styles.heroVisual}>
                    <img src={logo} alt="Combat TV logo" className={styles.logo} />
                </div>
            </section>

            <section className={styles.section}>
                <div className={styles.card}>
                    <div className={styles.cardHeader}>
                        <h2>Update password</h2>
                        <p>Enter your current password, then choose a new secure password.</p>
                    </div>

                    {errorMessage ? <div className={styles.formError}>{errorMessage}</div> : null}
                    {successMessage ? <div className={styles.formSuccess}>{successMessage}</div> : null}

                    <form className={styles.form} onSubmit={handleChangePassword}>
                        <div className={styles.fieldGroup}>
                            <label htmlFor="current-password">Current password</label>
                            <input
                                id="current-password"
                                type="password"
                                value={currentPassword}
                                onChange={(event) => {
                                    setCurrentPassword(event.target.value);
                                    setErrorMessage(null);
                                }}
                                autoComplete="current-password"
                                required
                            />
                        </div>

                        <div className={styles.fieldGroup}>
                            <label htmlFor="new-password">New password</label>
                            <input
                                id="new-password"
                                type="password"
                                value={newPassword}
                                onChange={(event) => {
                                    setNewPassword(event.target.value);
                                    setErrorMessage(null);
                                }}
                                autoComplete="new-password"
                                pattern={passwordComplexityRule.source}
                                title={passwordRequirementsMessage}
                                required
                            />
                            <p className={styles.helperText}>{passwordRequirementsMessage}</p>
                        </div>

                        <div className={styles.fieldGroup}>
                            <label htmlFor="confirm-password">Confirm new password</label>
                            <input
                                id="confirm-password"
                                type="password"
                                value={confirmPassword}
                                onChange={(event) => {
                                    setConfirmPassword(event.target.value);
                                    setErrorMessage(null);
                                }}
                                autoComplete="new-password"
                                required
                            />
                        </div>

                        {!passwordsMatch && confirmPassword.length > 0 ? (
                            <p className={styles.fieldError}>Passwords do not match.</p>
                        ) : null}

                        <div className={styles.actions}>
                            <button
                                type="submit"
                                className={styles.primaryButton}
                                disabled={loading || !passwordsMatch}
                            >
                                {loading ? 'Updating...' : 'Change password'}
                            </button>
                            <button
                                type="button"
                                className={styles.secondaryButton}
                                onClick={() => navigate(-1)}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <Link to={Routes.PROFILE} className={styles.tertiaryLink}>
                                Back to profile
                            </Link>
                        </div>
                    </form>
                </div>
            </section>
        </main>
    );
};

export default ChangePassword;
